import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { api } from "../../lib/api";

function useDebounce(value, delay = 500) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

const STATUS_STYLES = {
  failed: "bg-red-100 text-red-700",
  retrying: "bg-amber-100 text-amber-700",
  resolved: "bg-green-100 text-green-700",
};

// Legs label from the booking payload's journey_type (1 One way, 2 Round trip,
// 3 Multi city). A round trip with a single attempt row is a combined fare;
// two rows means separate onward/return legs. Falls back to the row count
// when journey_type is missing.
function legsLabel(g) {
  const jt = Number(g.journey_type);
  if (jt === 1) return "One way";
  if (jt === 2) return g.leg_count > 1 ? `Round trip (${g.leg_count})` : "Round trip (combined)";
  if (jt === 3) return "Multi city";
  return g.leg_count === 2 ? "Round trip (2)" : "One way (1)";
}

export default function BookingAttemptsList() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 20, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  const fetchGroups = async (opts = {}) => {
    const nextPage = opts.page ?? page;
    const nextLimit = opts.limit ?? limit;
    setLoading(true);
    setErr("");
    try {
      const res = await api.get("/admin/booking-attempts", {
        params: { search: debouncedSearch || undefined, page: nextPage, limit: nextLimit },
      });
      setGroups(res.data?.data || []);
      setMeta(res.data?.meta || { total: 0, page: nextPage, limit: nextLimit, totalPages: 1 });
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Failed to load booking attempts";
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPage(1);
    fetchGroups({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const goPrev = () => {
    if (meta.page <= 1) return;
    const p = meta.page - 1;
    setPage(p);
    fetchGroups({ page: p });
  };

  const goNext = () => {
    if (meta.page >= meta.totalPages) return;
    const p = meta.page + 1;
    setPage(p);
    fetchGroups({ page: p });
  };

  const onLimitChange = (e) => {
    const v = Number(e.target.value) || 20;
    setLimit(v);
    setPage(1);
    fetchGroups({ page: 1, limit: v });
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Booking Attempts</h1>
          <p className="mt-1 text-sm text-gray-600">
            Last 7 days &middot; checkout attempts whose trace_id never made it into Bookings, or only as a pending booking.
          </p>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-end gap-3 border rounded-2xl p-4">
        <div className="w-full sm:w-auto sm:min-w-[280px]">
          <label className="text-sm font-semibold">Search</label>
          <input
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            placeholder="Search by trace_id / payment_id / user_id"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="w-full sm:w-auto sm:min-w-[140px]">
          <label className="text-sm font-semibold">Per page</label>
          <select
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            value={limit}
            onChange={onLimitChange}
          >
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
      </div>

      {err ? <div className="mt-4 text-sm text-red-600">{err}</div> : null}

      <div className="mt-6 border rounded-2xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 text-sm font-semibold flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <span>Attempt List</span>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <button onClick={goPrev} disabled={meta.page <= 1 || loading} className="rounded-lg border px-3 py-1 font-semibold disabled:opacity-50" type="button">
              Prev
            </button>
            <span className="text-gray-700">
              Page <b>{meta.page}</b> / <b>{meta.totalPages}</b> (Total: <b>{meta.total}</b>)
            </span>
            <button onClick={goNext} disabled={meta.page >= meta.totalPages || loading} className="rounded-lg border px-3 py-1 font-semibold disabled:opacity-50" type="button">
              Next
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-4 text-sm text-gray-600">Loading...</div>
        ) : groups.length === 0 ? (
          <div className="p-4 text-sm text-gray-600">No unresolved booking attempts in the last 7 days.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-t bg-white">
                <tr className="text-left">
                  <th className="px-4 py-3">Trace ID</th>
                  <th className="px-4 py-3">User ID</th>
                  <th className="px-4 py-3">Payment ID</th>
                  <th className="px-4 py-3">Trip Indicator</th>
                  <th className="px-4 py-3">Legs</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="border-t">
                {groups.map((g) => {
                  const primary = g.attempts?.[0];
                  if (!primary) return null;
                  return (
                    <tr key={g.trace_id} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-xs">{g.trace_id}</td>
                      <td className="px-4 py-3">{primary.user_id || "-"}</td>
                      <td className="px-4 py-3">{primary.payment_id || "-"}</td>
                      <td className="px-4 py-3">{g.trip_indicator_id ?? "-"}</td>
                      <td className="px-4 py-3">{legsLabel(g)}</td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2 py-1 text-xs font-semibold ${STATUS_STYLES[primary.status] || "bg-gray-100 text-gray-700"}`}>
                          {primary.status}
                        </span>
                        {g.has_pending_booking ? (
                          <span className="ml-2 rounded-full px-2 py-1 text-xs font-semibold bg-amber-100 text-amber-700">
                            pending booking
                          </span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3">{primary.created_at ? new Date(primary.created_at).toLocaleString() : "-"}</td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          className="rounded-lg border px-3 py-1 text-xs font-semibold hover:bg-gray-50"
                          onClick={() => navigate(`/admin/booking-attempts/${g.trace_id}`)}
                        >
                          {g.has_pending_booking ? "Update Booking" : "Create Booking"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
